/** @jsxImportSource ./simboluri-jsx */
// REAZEMUL ARTICULAT — secțiunea paginii de element pentru o PILĂ (stâlp care
// nu coboară la fundație, ci se reazemă pe o grindă a nivelului de dedesubt)
// și pentru GAZDA ei (grinda de transfer care o poartă).
//
// De ce e o secțiune și nu o notă: la o pilă TOT calculul se schimbă față de
// un stâlp obișnuit — baza e articulată (M = 0), rigiditatea ei de cadru e
// a = 0,5·k̄/(1+2·k̄) în loc de k̄/(2+k̄), iar în gazdă intră o forță
// concentrată pe care grinda n-o avea din planșeu. Un desen e singurul mod în
// care cele trei lucruri se văd deodată.
//
// Același desen pe amândouă paginile, privit din partea elementului: pe pagina
// pilei e apăsată pila (N și V la bază, momentul de sus), pe pagina gazdei e
// apăsată grinda (forța concentrată, brațele a și b, deschiderea L).
//
// Ce NU face: nu calculează rigidități și nu inventează cifre. Identitatea
// vine din modelul Pasului 7 (`articulatii`, `stalpi_plantati`,
// `grinzi_transfer`), cifrele din Pasul 13 (`transfer` pe grinda-gazdă). Pe un
// rezultat vechi, fără cifre, desenul rămâne fără ele.

import { FemModel, TransferGrinda } from "./tipuri";
import { useT, useTn, useTx } from "../i18n";
import { CotaH, mFmt } from "./cote";
import { PAL } from "./paletaDesen";
import { hashElement } from "./legaturi";

/** Rolul elementului în perechea pilă–gazdă, sau `null` pentru un element
 *  obișnuit (stâlp la fundație, grindă care nu poartă nimic). */
export type RolReazem =
  | { rol: "pila"; stalp: string; gazda: string; x_m?: number }
  | { rol: "gazda"; grinda: string; pile: { stalp: string; x_m?: number }[] };

/** Cine e `ref` în modelul FEM: pilă, gazdă sau nimic din astea.
 *
 *  Citește cele trei liste în ordinea încrederii: `articulatii` e chiar legătura
 *  cu rotire liberă din model; `stalpi_plantati` și `grinzi_transfer` o
 *  confirmă cu `articulat` / `rezemat_pe_grinda`. Un stâlp plantat pe o grindă
 *  FĂRĂ aceste steaguri nu e pilă — e o coincidență de plan — și nu primește
 *  secțiunea. */
export function reazemulArticulat(
  ref: string,
  model?: FemModel | null
): RolReazem | null {
  if (!model || !ref) return null;
  const articulatii = model.articulatii ?? [];
  const plantati = model.stalpi_plantati ?? [];
  const transfer = model.grinzi_transfer ?? {};
  const xPe = (stalp: string, grinda: string): number | undefined =>
    plantati.find((p) => p.stalp === stalp && p.pe_grinda === grinda)?.x_m
    ?? (transfer[grinda] ?? []).find((p) => p.stalp === stalp)?.x_m;

  const art = articulatii.find((a) => a.stalp === ref);
  if (art) {
    return { rol: "pila", stalp: ref, gazda: art.gazda, x_m: xPe(ref, art.gazda) };
  }
  const plantat = plantati.find(
    (p) => p.stalp === ref && (p.articulat || p.rezemat_pe_grinda));
  if (plantat) {
    return { rol: "pila", stalp: ref, gazda: plantat.pe_grinda, x_m: plantat.x_m };
  }

  const pile = new Map<string, number | undefined>();
  for (const a of articulatii) {
    if (a.gazda === ref) pile.set(a.stalp, xPe(a.stalp, ref));
  }
  for (const p of plantati) {
    if (p.pe_grinda === ref && (p.articulat || p.rezemat_pe_grinda)) {
      pile.set(p.stalp, p.x_m ?? pile.get(p.stalp));
    }
  }
  for (const p of transfer[ref] ?? []) {
    if (p.rezemat_pe_grinda) pile.set(p.stalp, p.x_m ?? pile.get(p.stalp));
  }
  if (pile.size === 0) return null;
  return {
    rol: "gazda",
    grinda: ref,
    pile: [...pile.entries()].map(([stalp, x_m]) => ({ stalp, x_m })),
  };
}

// ---------------------------------------------------------------------------
// Desenul
// ---------------------------------------------------------------------------

const W = 480;
const H = 262;
const X0 = 70;          // reazemul i al gazdei
const X1 = 410;         // reazemul j al gazdei
const YG = 172;         // fața de sus a grinzii-gazdă
const HG = 18;          // înălțimea desenată a gazdei
const YSUS = 34;        // fața de sus a grinzii etajului retras
const BP = 14;          // lățimea desenată a pilei
// forțele și marcajul articulației sunt NOTAȚIE, nu materie: accentul
// interfeței, ca la cotele despre care e desenul
const FORTA = "var(--acc, #0b7f92)";

/** Săgeată dreaptă de la (x1,y1) la (x2,y2), cu vârful la capătul 2. */
function Sageata({ x1, y1, x2, y2, culoare = FORTA }: {
  x1: number; y1: number; x2: number; y2: number; culoare?: string;
}) {
  const dx = x2 - x1, dy = y2 - y1, L = Math.hypot(dx, dy) || 1;
  const ux = dx / L, uy = dy / L;
  const a = 7, b = 3.5;
  const bx = x2 - ux * a, by = y2 - uy * a;
  return (
    <g stroke={culoare} fill={culoare} strokeWidth={1.6}>
      <line x1={x1} y1={y1} x2={bx} y2={by} />
      <polygon points={`${x2},${y2} ${bx - uy * b},${by + ux * b} ${bx + uy * b},${by - ux * b}`}
               stroke="none" />
    </g>
  );
}

/** Săgeată curbă (momentul) în jurul punctului (cx, cy), sens orar. */
function SageataMoment({ cx, cy, r = 16, culoare = FORTA }: {
  cx: number; cy: number; r?: number; culoare?: string;
}) {
  // arc de 240°, de la −210° la +30°, cu vârful la capătul din dreapta-jos
  const a0 = (-210 * Math.PI) / 180, a1 = (30 * Math.PI) / 180;
  const p0 = [cx + r * Math.cos(a0), cy + r * Math.sin(a0)];
  const p1 = [cx + r * Math.cos(a1), cy + r * Math.sin(a1)];
  // tangenta la capăt (sens orar în coordonate ecran = unghi crescător)
  const tx = -Math.sin(a1), ty = Math.cos(a1);
  const a = 7, b = 3.5;
  const bx = p1[0] - tx * a, by = p1[1] - ty * a;
  return (
    <g stroke={culoare} fill="none" strokeWidth={1.6}>
      <path d={`M ${p0[0]},${p0[1]} A ${r},${r} 0 1 1 ${p1[0]},${p1[1]}`} />
      <polygon points={`${p1[0]},${p1[1]} ${bx - ty * b},${by + tx * b} ${bx + ty * b},${by - tx * b}`}
               fill={culoare} stroke="none" />
    </g>
  );
}

/** Reazemul unei grinzi: triunghiul clasic, cu vârful în grindă. */
function Reazem({ x, y, eticheta }: { x: number; y: number; eticheta?: string | null }) {
  return (
    <g>
      <polygon points={`${x},${y} ${x - 9},${y + 14} ${x + 9},${y + 14}`}
               fill="none" stroke={PAL.ink} strokeWidth={1.2} />
      <line x1={x - 13} y1={y + 14} x2={x + 13} y2={y + 14}
            stroke={PAL.ink} strokeWidth={1.2} />
      {eticheta && (
        <text x={x} y={y + 26} fontSize={9.5} textAnchor="middle" fill={PAL.mut}>
          {eticheta}
        </text>
      )}
    </g>
  );
}

/** Desenul comun. `apasat` spune din partea cui se privește: pila sau gazda.
 *  `pile` sunt pilele de pe gazdă, cu abscisa lor pe grindă (metri de la
 *  reazemul i) când se cunoaște; fără ea, pila se desenează la mijloc. */
function DesenReazem({
  apasat,
  pile,
  transfer,
}: {
  apasat: "pila" | "gazda";
  pile: { stalp: string; x_m?: number; R_d_kn?: number }[];
  transfer?: TransferGrinda | null;
}) {
  const t = useT();
  const L = transfer?.L_m;
  const pxLa = (x_m?: number) =>
    L && x_m != null && L > 0
      ? X0 + Math.min(Math.max(x_m / L, 0.05), 0.95) * (X1 - X0)
      : (X0 + X1) / 2;
  const tonPila = apasat === "pila" ? 1 : 0.55;
  const tonGazda = apasat === "gazda" ? 1 : 0.55;
  // o singură pilă apăsată primește toată notația (N, V, M, articulația);
  // mai multe pile pe aceeași gazdă primesc fiecare articulația și N
  const detaliata = pile.length === 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W }}>
      {/* grinda etajului retras, în care se prinde capul pilei */}
      {pile.map((p) => {
        const x = pxLa(p.x_m);
        return (
          <g key={`sus-${p.stalp}`} opacity={tonPila}>
            <rect x={x - 66} y={YSUS} width={132} height={14}
                  fill={PAL.beton} stroke={PAL.grinda} strokeWidth={1.1}
                  strokeDasharray="5 3" />
            {detaliata && (
              <text x={x + 72} y={YSUS + 11} fontSize={9.5} fill={PAL.mut}>
                grinda etajului retras
              </text>
            )}
          </g>
        );
      })}
      {/* pilele */}
      {pile.map((p) => {
        const x = pxLa(p.x_m);
        return (
          <g key={p.stalp} opacity={tonPila}>
            <rect x={x - BP / 2} y={YSUS + 14} width={BP} height={YG - YSUS - 14}
                  fill={PAL.beton} stroke={PAL.stalp} strokeWidth={1.3} />
            <text x={x + BP / 2 + 6} y={(YSUS + YG) / 2 - 6} fontSize={10.5}
                  fontWeight={600} fill={PAL.stalp}>
              {p.stalp}
            </text>
            <text x={x + BP / 2 + 6} y={(YSUS + YG) / 2 + 6} fontSize={9} fill={PAL.mut}>
              {t("struct.rez.pila")}
            </text>
            {/* momentul de sus: pila e o pârghie prinsă în grinda de deasupra */}
            {detaliata && (
              <>
                <SageataMoment cx={x} cy={YSUS + 7} r={20} />
                <text x={x - 30} y={YSUS - 12} fontSize={9.5} fill={FORTA}
                      textAnchor="end">
                  {t("struct.rez.M_sus")}
                </text>
              </>
            )}
            {/* articulația: cerc gol, M = 0 */}
            <circle cx={x} cy={YG} r={5} fill="#fff" stroke={PAL.nodReazem}
                    strokeWidth={1.6} />
            <text x={x + 10} y={YG + HG + 13} fontSize={10} fontWeight={600}
                  fill={PAL.nodReazem}>
              M = 0
            </text>
            {/* N coboară în gazdă */}
            <Sageata x1={x - BP / 2 - 12} y1={YG - 56} x2={x - BP / 2 - 12} y2={YG - 8} />
            <text x={x - BP / 2 - 18} y={YG - 40} fontSize={10} fill={FORTA}
                  textAnchor="end">
              N{p.R_d_kn != null ? ` = ${mFmt(p.R_d_kn, 0)} kN` : ""}
            </text>
            {/* V pleacă în planșeu, nu în grindă */}
            {detaliata && (
              <>
                <Sageata x1={x + BP / 2 + 62} y1={YG - 12} x2={x + BP / 2 + 22} y2={YG - 12} />
                <text x={x + BP / 2 + 66} y={YG - 9} fontSize={9.5} fill={FORTA}>
                  {t("struct.rez.V_planseu")}
                </text>
              </>
            )}
          </g>
        );
      })}
      {/* grinda-gazdă, cu reazemele ei */}
      <g opacity={tonGazda}>
        <rect x={X0} y={YG} width={X1 - X0} height={HG}
              fill={PAL.beton} stroke={PAL.grinda} strokeWidth={1.3} />
        <Reazem x={X0} y={YG + HG} eticheta={transfer?.stalp_i ?? t("struct.rez.reazem_i")} />
        <Reazem x={X1} y={YG + HG} eticheta={transfer?.stalp_j ?? t("struct.rez.reazem_j")} />
        {apasat === "pila" && (
          <text x={X1 - 4} y={YG - 6} fontSize={10} fill={PAL.grinda}
                textAnchor="end" fontWeight={600}>
            {t("struct.rez.gazda_transfer")}
          </text>
        )}
      </g>
      {/* cotele gazdei: a, b, L — doar cu cifre reale */}
      {apasat === "gazda" && L != null && L > 0 && pile.length === 1
        && pile[0].x_m != null && (
        <>
          <CotaH x1={X0} x2={pxLa(pile[0].x_m)} y={YG + HG + 44}
                 martorY={YG + HG} text={`a = ${mFmt(pile[0].x_m)} m`} size={9.5} />
          <CotaH x1={pxLa(pile[0].x_m)} x2={X1} y={YG + HG + 44}
                 martorY={YG + HG} text={`b = ${mFmt(L - pile[0].x_m)} m`} size={9.5} />
        </>
      )}
      {L != null && L > 0 && (
        <CotaH x1={X0} x2={X1} y={YG + HG + 66} martorY={YG + HG}
               text={`L = ${mFmt(L)} m`} culoare={apasat === "gazda" ? FORTA : undefined} />
      )}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Secțiunea
// ---------------------------------------------------------------------------

export default function ReazemArticulat({
  refElement,
  model,
  transfer,
  projectId,
  solutie,
}: {
  refElement: string;
  model?: FemModel | null;
  /** `transfer` al grinzii-GAZDĂ (Pasul 13) — al ei însăși pe pagina gazdei,
   *  al gazdei pe pagina pilei. Lipsește pe rezultatele fără Pasul 13. */
  transfer?: TransferGrinda | null;
  projectId: string;
  solutie?: string | null;
}) {
  const t = useT();
  const tn = useTn();
  const tx = useTx();
  const rol = reazemulArticulat(refElement, model);
  if (!rol) return null;
  const link = (ref: string) => (
    <a href={hashElement(projectId, ref, solutie)} title={t("struct.rez.link_element", { ref })}>
      {ref}
    </a>
  );
  const f = transfer?.factor_amplificare;
  const randPila = (stalp: string) => transfer?.stalpi?.find((s) => s.stalp === stalp);

  if (rol.rol === "pila") {
    const rand = randPila(rol.stalp);
    return (
      <div className="ob-card" style={{ padding: "11px 14px" }}>
        <span className="ob-lbl" style={{ display: "block", paddingBottom: 6 }}>
          {t("struct.rez.titlu_pila", { gazda: "" })}{link(rol.gazda)}
        </span>
        <DesenReazem apasat="pila" transfer={transfer}
                     pile={[{ stalp: rol.stalp, x_m: rand?.d_reazem_i_m ?? rol.x_m,
                              R_d_kn: rand?.R_d_kn }]} />
        <div style={{ fontSize: 12, color: "var(--ink)", display: "flex",
                      flexDirection: "column", gap: 6, marginTop: 6 }}>
          <div>
            {tx("struct.rez.pila_text", { stalp: rol.stalp, gazda: rol.gazda })}
            {rand?.R_d_kn != null ? <> = {mFmt(rand.R_d_kn, 0)} kN</> : null}
            {tx("struct.rez.pila_text_v")}
          </div>
          <div>
            {t("struct.rez.rigiditate")}
            <span className="sg-mono">D = a·12EI<sub>c</sub>/h³</span>,{" "}
            <span className="sg-mono">a = 0,5·k̄/(1+2·k̄)</span>
            {t("struct.rez.rigiditate_nota")}
          </div>
          {!transfer && (
            <div className="sg-muted" style={{ fontSize: 11 }}>
              {t("struct.rez.fara_p13")}
            </div>
          )}
        </div>
      </div>
    );
  }

  // GAZDA
  const L = transfer?.L_m;
  const pile = rol.pile.map((p) => {
    const rand = randPila(p.stalp);
    return { stalp: p.stalp, x_m: rand?.d_reazem_i_m ?? p.x_m,
             R_kn: rand?.R_kn, R_d_kn: rand?.R_d_kn, b_m: rand?.d_reazem_j_m };
  });
  return (
    <div className="ob-card" style={{ padding: "11px 14px" }}>
      <span className="ob-lbl" style={{ display: "block", paddingBottom: 6 }}>
        {tn("struct.rez.titlu_gazda#n", pile.length)}{" "}
        {pile.map((p, i) => <span key={p.stalp}>{i > 0 ? ", " : ""}{link(p.stalp)}</span>)}
      </span>
      <DesenReazem apasat="gazda" transfer={transfer} pile={pile} />
      <div style={{ fontSize: 12, color: "var(--ink)", display: "flex",
                    flexDirection: "column", gap: 6, marginTop: 6 }}>
        <div>
          {tx("struct.rez.gazda_text")}
        </div>
        <table className="sg-table" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>{t("struct.rez.col_pila")}</th>
              <th title={t("struct.rez.col_R_titlu")}>R</th>
              <th title={t("struct.rez.col_Rd_titlu")}>R<sub>d</sub></th>
              <th title={t("struct.rez.col_a_titlu")}>a</th>
              <th title={t("struct.rez.col_b_titlu")}>b</th>
              <th title={t("struct.rez.col_spor_titlu")}>{t("struct.rez.col_spor")}</th>
            </tr>
          </thead>
          <tbody>
            {pile.map((p) => {
              const R = p.R_kn ?? (p.R_d_kn != null && f ? p.R_d_kn / f : undefined);
              const a = p.x_m;
              const b = p.b_m ?? (L != null && a != null ? L - a : undefined);
              const dM = R != null && a != null && b != null && L && f != null
                ? (R * a * b) / L * f : null;
              return (
                <tr key={p.stalp}>
                  <td>{link(p.stalp)}</td>
                  <td className="sg-mono">{R != null ? `${mFmt(R, 0)} kN` : "—"}</td>
                  <td className="sg-mono">{p.R_d_kn != null ? `${mFmt(p.R_d_kn, 0)} kN` : "—"}</td>
                  <td className="sg-mono">{a != null ? `${mFmt(a)} m` : "—"}</td>
                  <td className="sg-mono">{b != null ? `${mFmt(b)} m` : "—"}</td>
                  <td className="sg-mono">
                    {dM != null
                      ? `${mFmt(R!, 0)}·${mFmt(a!)}·${mFmt(b!)}/${mFmt(L!)}·${mFmt(f!, 2)} = ${mFmt(dM, 0)} kN·m`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="sg-muted" style={{ fontSize: 11 }}>
          {t("struct.rez.nota_f", { f: f != null ? mFmt(f, 2) : "—" })}
          {!transfer && t("struct.rez.nota_fara_p13")}
        </div>
      </div>
    </div>
  );
}
